import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiTruck, FiDownload, FiPrinter, FiSearch, FiFileText,
  FiCheckCircle, FiClock, FiXCircle, FiDollarSign,
  FiChevronDown, FiChevronRight, FiExternalLink
} from 'react-icons/fi';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import useReportMemory from '../../hooks/useReportMemory';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
  '#4A90D9',
  '#E67E22',
  '#2ECC71',
  '#9B59B6'
];

const STATUS_CONFIG = {
  Draft: { color: '#6C757D', bg: '#F1F3F5', label: 'Draft' },
  Approved: { color: '#17A2B8', bg: '#E3F6F9', label: 'Approved' },
  'Partially Delivered': { color: '#FFC107', bg: '#FFF8E1', label: 'Partially Delivered' },
  Completed: { color: '#28A745', bg: '#E8F5E9', label: 'Completed' },
  Closed: { color: '#546E7A', bg: '#ECEFF1', label: 'Closed' },
  Cancelled: { color: '#DC3545', bg: '#FFEBEE', label: 'Cancelled' }
};

// FLW-06: only active documents count — challans that are not Draft / Cancelled,
// invoices that are Posted / Partial / Paid. 'Delivered' is an old SO status
// (written by earlier versions of the challan screen) that means Completed.
const DC_ACTIVE = (s) => !!s && s !== 'Draft' && s !== 'Cancelled';
const INVOICE_LIVE = ['Posted', 'Partial', 'Paid'];
const soStatusOf = (s) => (s === 'Delivered' ? 'Completed' : s);
const qtyOfLines = (lines) => (lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getMonthKey = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthLabel = (key) => {
  if (!key || key === 'Unknown') return 'Unknown';
  const [year, month] = key.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
};

const SOTracking = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const [savedMem, saveMemory] = useReportMemory('so-tracking');

  // Data states
  const [soTransactions, setSoTransactions] = useState(() => savedMem?.soTransactions ?? []);
  const [dcTransactions, setDcTransactions] = useState(() => savedMem?.dcTransactions ?? []);
  const [siTransactions, setSiTransactions] = useState(() => savedMem?.siTransactions ?? []);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedCustomer, setSelectedCustomer] = useState(() => savedMem?.selectedCustomer ?? '');
  const [selectedSalesperson, setSelectedSalesperson] = useState(() => savedMem?.selectedSalesperson ?? '');
  const [selectedStatus, setSelectedStatus] = useState(() => savedMem?.selectedStatus ?? 'All');
  const [selectedFulfillment, setSelectedFulfillment] = useState(() => savedMem?.selectedFulfillment ?? 'All');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(() => savedMem?.selectedRegionId ?? 'all');
  const [selectedAreaId, setSelectedAreaId] = useState(() => savedMem?.selectedAreaId ?? 'all');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');
  const [expandedRows, setExpandedRows] = useState(() => savedMem?.expandedRows ?? {});

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  // Load customers
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const custRef = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(custRef, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  // Load employees (salespersons)
  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const empRows = await getEmployeesCached(activeCompany.id);
      setEmployees(empRows.filter(r => r.firstName != null)
        .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || ''))));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [activeCompany]);

  // Load regions
  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const snapshot = await getDocs(ref);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  // Load areas
  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const snapshot = await getDocs(ref);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadCustomers();
    loadEmployees();
    loadRegions();
    loadAreas();

    // Set default date range
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadFinancialYears, loadCustomers, loadEmployees, loadRegions, loadAreas, savedMem]);

  // ── Backward memory: persist filters + generated rows (display-only) ──
  useEffect(() => {
    if (!hasSearched) return;
    saveMemory({
      selectedFYId, dateFrom, dateTo, selectedCustomer, selectedSalesperson,
      selectedStatus, selectedFulfillment, selectedRegionId, selectedAreaId,
      sortField, sortDirection,
      soTransactions, dcTransactions, siTransactions,
      expandedRows, hasSearched
    });
  }, [hasSearched, selectedFYId, dateFrom, dateTo, selectedCustomer, selectedSalesperson,
      selectedStatus, selectedFulfillment, selectedRegionId, selectedAreaId, sortField, sortDirection,
      soTransactions, dcTransactions, siTransactions, expandedRows, saveMemory]);

  // Load transactions
  const loadTransactions = async () => {
    if (!activeCompany?.id || !selectedFYId) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');

      // Load SO transactions
      const soQuery = query(transRef,
        where('tranTypeCode', '==', 'SO'),
        where('financialYearId', '==', selectedFYId)
      );
      const soSnapshot = await getDocs(soQuery);
      const soData = soSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      soData.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setSoTransactions(soData);

      // Collect SO IDs for child lookup
      const soIds = soData.map(so => so.id);
      const inBranch = (t) => (!activeBranch || activeBranch.id === 'all') || t.branchId === activeBranch.id;
      const childrenOf = async (parentIds) => {
        // Firestore 'in' query supports max 30 items per batch
        const batches = [];
        for (let i = 0; i < parentIds.length; i += 30) {
          batches.push(getDocs(query(transRef, where('parentId', 'in', parentIds.slice(i, i + 30)))));
        }
        const out = [];
        (await Promise.all(batches)).forEach(snap => {
          snap.docs.forEach(d => out.push({ id: d.id, ...d.data() }));
        });
        return out;
      };

      // Documents linked to these SOs: Delivery Challans and invoices made
      // straight from an SO (one query per 30 SOs).
      const soChildren = soIds.length > 0 ? await childrenOf(soIds) : [];
      const dcData = soChildren.filter(t => t.tranTypeCode === 'DC');
      const directInvoices = soChildren.filter(t => t.tranTypeCode === 'SI' || t.tranTypeCode === 'NSI');

      // FLW-06: invoices made from those challans (SO → DC → SI).
      const activeDcIds = dcData.filter(t => DC_ACTIVE(t.status)).map(t => t.id);
      const dcInvoices = (activeDcIds.length > 0 ? await childrenOf(activeDcIds) : [])
        .filter(t => t.tranTypeCode === 'SI' || t.tranTypeCode === 'NSI');

      setDcTransactions(dcData.filter(inBranch));
      setSiTransactions([...directInvoices, ...dcInvoices].filter(inBranch));
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build DC and SI lookup maps (FLW-06: SO → DC → SI chain, active documents only)
  const dcByParent = useMemo(() => {
    const map = {};
    dcTransactions.forEach(dc => {
      if (!dc.parentId || !DC_ACTIVE(dc.status)) return;
      if (!map[dc.parentId]) map[dc.parentId] = [];
      map[dc.parentId].push(dc);
    });
    return map;
  }, [dcTransactions]);

  // Invoices per SO. A line belongs to the SO the invoice was made from directly,
  // or to the SO of the challan it came from (its source challan, else the
  // invoice's parent challan). Each entry carries that SO's share: soLines,
  // soQty, soAmount (invoice total split by line value), direct (no challan).
  const siByParent = useMemo(() => {
    const soIdSet = new Set(soTransactions.map(so => so.id));
    const dcToSo = {};
    Object.entries(dcByParent).forEach(([soId, dcs]) => dcs.forEach(dc => { dcToSo[dc.id] = soId; }));
    const map = {};
    siTransactions.forEach(si => {
      if (!INVOICE_LIVE.includes(si.status)) return;
      const allLines = si.lines || [];
      const perSo = {};
      if (soIdSet.has(si.parentId)) {
        perSo[si.parentId] = allLines;
      } else {
        allLines.forEach(l => {
          const soId = dcToSo[l.sourceDocId || si.parentId];
          if (!soId) return;
          if (!perSo[soId]) perSo[soId] = [];
          perSo[soId].push(l);
        });
      }
      const invTotal = Number(si.totalAmount) || Number(si.netAmount) || 0;
      const allValue = allLines.reduce((s, l) => s + (Number(l.lineTotal) || 0), 0);
      Object.entries(perSo).forEach(([soId, lines]) => {
        const value = lines.reduce((s, l) => s + (Number(l.lineTotal) || 0), 0);
        const share = lines.length === allLines.length ? 1
          : (allValue > 0 ? value / allValue : lines.length / (allLines.length || 1));
        if (!map[soId]) map[soId] = [];
        map[soId].push({ ...si, soLines: lines, soQty: qtyOfLines(lines), soAmount: invTotal * share, direct: si.parentId === soId });
      });
    });
    return map;
  }, [soTransactions, siTransactions, dcByParent]);

  // Enriched SO data with fulfillment info
  const enrichedSOs = useMemo(() => {
    return soTransactions.map(so => {
      const dcs = dcByParent[so.id] || [];
      const sis = siByParent[so.id] || [];
      const directSis = sis.filter(si => si.direct);

      // Calculate total SO qty from lines
      const soTotalQty = (so.lines || []).reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);

      // Delivered = active challans + invoices made straight from the SO (no challan)
      const dcTotalQty = dcs.reduce((sum, dc) => sum + qtyOfLines(dc.lines), 0)
        + directSis.reduce((sum, si) => sum + si.soQty, 0);

      // Invoiced = posted invoices made from the SO or from its challans
      const siTotalQty = sis.reduce((sum, si) => sum + si.soQty, 0);

      // Delivered amount: challans + direct invoices
      const dcTotalAmount = dcs.reduce((sum, dc) => sum + (Number(dc.totalAmount) || Number(dc.netAmount) || 0), 0)
        + directSis.reduce((sum, si) => sum + si.soAmount, 0);

      // Invoiced amount (an invoice covering several SOs is split by line value)
      const siTotalAmount = sis.reduce((sum, si) => sum + si.soAmount, 0);

      // Fulfillment % — delivered qty; the SO's own deliveredQty (kept by the
      // challan / invoice screens) is the fallback when no linked document loaded.
      const fulfilledQty = dcTotalQty > 0 ? dcTotalQty
        : (so.lines || []).reduce((sum, line) => sum + (Number(line.deliveredQty) || 0), 0);
      const fulfillmentPct = soTotalQty > 0 ? Math.min((fulfilledQty / soTotalQty) * 100, 100) : 0;

      // Invoice %
      const soAmount = Number(so.totalAmount) || Number(so.netAmount) || 0;
      const invoicePct = soAmount > 0 ? Math.min((siTotalAmount / soAmount) * 100, 100) : 0;

      // Build item-wise tracking: aggregate SO qty, DC qty, SI qty per item
      const itemMap = {};
      (so.lines || []).forEach(line => {
        const key = line.itemId || line.description || `line-${line.lineNumber}`;
        itemMap[key] = {
          itemId: line.itemId || '',
          itemCode: line.itemCode || '',
          itemName: line.itemName || line.description || '',
          uom: line.unitOfMeasure || '',
          orderedQty: Number(line.quantity) || 0,
          deliveredQty: 0,
          invoicedQty: 0,
          unitPrice: line.unitPrice || 0,
          orderedAmount: line.lineTotal || ((Number(line.quantity) || 0) * (line.unitPrice || 0))
        };
      });

      [...dcs.map(dc => dc.lines || []), ...directSis.map(si => si.soLines)].forEach(dcLines => {
        dcLines.forEach(line => {
          const key = line.itemId || line.description || `line-${line.lineNumber}`;
          if (itemMap[key]) {
            itemMap[key].deliveredQty += (Number(line.quantity) || 0);
          } else {
            itemMap[key] = {
              itemId: line.itemId || '',
              itemCode: line.itemCode || '',
              itemName: line.itemName || line.description || '',
              uom: line.unitOfMeasure || '',
              orderedQty: 0,
              deliveredQty: Number(line.quantity) || 0,
              invoicedQty: 0,
              unitPrice: line.unitPrice || 0,
              orderedAmount: 0
            };
          }
        });
      });

      sis.forEach(si => {
        (si.soLines || []).forEach(line => {
          const key = line.itemId || line.description || `line-${line.lineNumber}`;
          if (itemMap[key]) {
            itemMap[key].invoicedQty += (Number(line.quantity) || 0);
          } else {
            itemMap[key] = {
              itemId: line.itemId || '',
              itemCode: line.itemCode || '',
              itemName: line.itemName || line.description || '',
              uom: line.unitOfMeasure || '',
              orderedQty: 0,
              deliveredQty: 0,
              invoicedQty: Number(line.quantity) || 0,
              unitPrice: line.unitPrice || 0,
              orderedAmount: 0
            };
          }
        });
      });

      // A Sale Order closed by hand: what was left is closed, not pending.
      const soClosed = so.status === 'Closed';
      const itemBreakdown = Object.values(itemMap).map(item => ({
        ...item,
        pendingQty: soClosed ? 0 : Math.max(0, item.orderedQty - Math.max(item.deliveredQty, item.invoicedQty)),
        closedQty: soClosed ? Math.max(0, item.orderedQty - Math.max(item.deliveredQty, item.invoicedQty)) : 0,
        deliveredPct: item.orderedQty > 0 ? Math.min((item.deliveredQty / item.orderedQty) * 100, 100) : 0,
        invoicedPct: item.orderedQty > 0 ? Math.min((item.invoicedQty / item.orderedQty) * 100, 100) : 0
      }));

      return {
        ...so,
        soAmount,
        soTotalQty,
        dcCount: dcs.length,
        dcTotalQty,
        dcTotalAmount,
        siCount: sis.length,
        siTotalQty,
        siTotalAmount,
        fulfillmentPct: Math.round(fulfillmentPct * 100) / 100,
        invoicePct: Math.round(invoicePct * 100) / 100,
        dcs,
        sis,
        itemBreakdown
      };
    });
  }, [soTransactions, dcByParent, siByParent]);

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // Cascading areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Filtered transactions
  const filteredTransactions = useMemo(() => {
    let filtered = [...enrichedSOs];

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }

    // Customer filter
    if (selectedCustomer) {
      filtered = filtered.filter(t => t.partyId === selectedCustomer);
    }

    // Salesperson filter
    if (selectedSalesperson) {
      filtered = filtered.filter(t => t.employeeId === selectedSalesperson);
    }

    // Region filter
    if (selectedRegionId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.regionId === selectedRegionId;
      });
    }

    // Area filter
    if (selectedAreaId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.areaId === selectedAreaId;
      });
    }

    // Status filter
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(t => soStatusOf(t.status) === selectedStatus);
    }

    // Fulfillment filter
    if (selectedFulfillment !== 'All') {
      switch (selectedFulfillment) {
        case 'Open':
          filtered = filtered.filter(t => t.fulfillmentPct === 0 && t.status !== 'Cancelled' && t.status !== 'Closed');
          break;
        case 'Partially Fulfilled':
          filtered = filtered.filter(t => t.fulfillmentPct > 0 && t.fulfillmentPct < 100);
          break;
        case 'Fully Fulfilled':
          filtered = filtered.filter(t => t.fulfillmentPct >= 100);
          break;
        default:
          break;
      }
    }

    return filtered;
  }, [enrichedSOs, dateFrom, dateTo, selectedCustomer, selectedSalesperson, selectedStatus, selectedFulfillment, selectedRegionId, selectedAreaId, customerMap]);

  // Sorted transactions
  const sortedTransactions = useMemo(() => {
    const sorted = [...filteredTransactions];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (['soAmount', 'soTotalQty', 'dcTotalAmount', 'dcTotalQty', 'siTotalAmount', 'siTotalQty', 'fulfillmentPct', 'dcCount', 'siCount'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredTransactions, sortField, sortDirection]);

  // Summary calculations
  const summary = useMemo(() => {
    const nonCancelled = filteredTransactions.filter(t => t.status !== 'Cancelled');
    const totalSOs = filteredTransactions.length;
    const openSOs = filteredTransactions.filter(t =>
      t.status === 'Approved' || t.status === 'Draft'
    ).length;
    const partiallyDelivered = filteredTransactions.filter(t =>
      t.status === 'Partially Delivered'
    ).length;
    const completed = filteredTransactions.filter(t => soStatusOf(t.status) === 'Completed').length;
    const totalSOValue = nonCancelled.reduce((sum, t) => sum + (t.soAmount || 0), 0);

    return { totalSOs, openSOs, partiallyDelivered, completed, totalSOValue };
  }, [filteredTransactions]);

  // Pie chart: SO status distribution
  const statusChartData = useMemo(() => {
    const statusCounts = {};
    filteredTransactions.forEach(t => {
      const status = t.status || 'Unknown';
      if (!statusCounts[status]) {
        statusCounts[status] = { name: status, value: 0 };
      }
      statusCounts[status].value += 1;
    });
    return Object.values(statusCounts).sort((a, b) => b.value - a.value);
  }, [filteredTransactions]);

  // Bar chart: SO value by customer (top 10)
  const customerChartData = useMemo(() => {
    const customerValues = {};
    filteredTransactions.filter(t => t.status !== 'Cancelled').forEach(t => {
      const name = t.partyName || 'Unknown';
      if (!customerValues[name]) {
        customerValues[name] = { name, amount: 0 };
      }
      customerValues[name].amount += t.soAmount || 0;
    });
    return Object.values(customerValues)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [filteredTransactions]);

  // Line chart: SO trend over time (monthly)
  const trendData = useMemo(() => {
    const grouped = {};
    filteredTransactions.filter(t => t.status !== 'Cancelled').forEach(t => {
      const key = getMonthKey(t.docDate);
      if (!grouped[key]) {
        grouped[key] = { period: key, count: 0, value: 0 };
      }
      grouped[key].count += 1;
      grouped[key].value += t.soAmount || 0;
    });
    return Object.values(grouped)
      .sort((a, b) => a.period.localeCompare(b.period))
      .map(g => ({ ...g, label: getMonthLabel(g.period) }));
  }, [filteredTransactions]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Toggle expanded row
  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Navigate to SO
  const handleSOClick = (transaction) => {
    navigate('/sales/sale-order', { state: { viewTransactionId: transaction.id } });
  };

  // Navigate to DC
  const handleDCClick = (dc) => {
    navigate('/sales/delivery-challan', { state: { viewTransactionId: dc.id } });
  };

  // Navigate to SI
  const handleSIClick = (si) => {
    navigate('/sales/sale-invoice', { state: { viewTransactionId: si.id } });
  };

  // Fulfillment color
  const getFulfillmentColor = (pct) => {
    if (pct >= 100) return '#28A745';
    if (pct > 0) return '#FFC107';
    return '#DC3545';
  };

  // CSV Export
  const handleExport = () => {
    if (sortedTransactions.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'SO Tracking Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    csv += `Total SOs,${summary.totalSOs}\n`;
    csv += `Open SOs,${summary.openSOs}\n`;
    csv += `Partially Delivered,${summary.partiallyDelivered}\n`;
    csv += `Completed,${summary.completed}\n`;
    csv += `Total SO Value,${summary.totalSOValue}\n\n`;
    csv += 'Date,SO #,Customer,Salesperson,Ordered Qty,SO Amount,DC Count,Delivered Qty,Delivered Amt,Inv Count,Invoiced Qty,Invoiced Amt,Fulfillment %,Status\n';

    sortedTransactions.forEach(t => {
      csv += `"${formatDateStr(t.docDate)}","${t.docNumber || ''}",`;
      csv += `"${(t.partyName || '').replace(/"/g, '""')}","${(t.salesPersonName || '').replace(/"/g, '""')}",`;
      csv += `${t.soTotalQty || 0},${t.soAmount || 0},${t.dcCount || 0},${t.dcTotalQty || 0},${t.dcTotalAmount || 0},`;
      csv += `${t.siCount || 0},${t.siTotalQty || 0},${t.siTotalAmount || 0},`;
      csv += `${t.fulfillmentPct || 0}%,"${t.status || ''}"\n`;

      // Item-wise rows
      if (t.itemBreakdown && t.itemBreakdown.length > 0) {
        t.itemBreakdown.forEach(item => {
          csv += `,,  ${item.itemCode || ''} - ${item.itemName || ''},,`;
          csv += `${item.orderedQty},,`;
          csv += `,${item.deliveredQty},,,${item.invoicedQty},,`;
          csv += `${Math.max(item.deliveredPct, item.invoicedPct).toFixed(0)}%,`;
          csv += `${item.pendingQty > 0 ? item.pendingQty + ' pending' : item.closedQty > 0 ? item.closedQty + ' closed' : 'Done'}\n`;
        });
      }
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SO_Tracking_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>SO Tracking Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        .status{padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500}
        .progress-bar{height:6px;background:#eee;border-radius:3px;overflow:hidden;width:60px;display:inline-block;vertical-align:middle;margin-right:5px}
        .progress-fill{height:100%;border-radius:3px}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>SO Tracking Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)} |
        Records: ${sortedTransactions.length}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Total SOs</div><div class="summary-val">${summary.totalSOs}</div></div>
        <div class="summary-box"><div class="summary-label">Open SOs</div><div class="summary-val">${summary.openSOs}</div></div>
        <div class="summary-box"><div class="summary-label">Partially Delivered</div><div class="summary-val">${summary.partiallyDelivered}</div></div>
        <div class="summary-box"><div class="summary-label">Completed</div><div class="summary-val">${summary.completed}</div></div>
        <div class="summary-box"><div class="summary-label">Total SO Value</div><div class="summary-val">${formatAmount(summary.totalSOValue)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Date</th><th>SO #</th><th>Customer</th><th>Salesperson</th>
          <th class="right">Ord Qty</th><th class="right">SO Amount</th>
          <th>DC</th><th class="right">Del Qty</th><th class="right">Del Amt</th>
          <th>Inv</th><th class="right">Inv Qty</th><th class="right">Inv Amt</th>
          <th>Fulfillment %</th><th>Status</th>
        </tr></thead>
        <tbody>
          ${sortedTransactions.map(t => {
            let rows = `
            <tr>
              <td>${formatDateStr(t.docDate)}</td>
              <td>${t.docNumber || '-'}</td>
              <td>${t.partyName || '-'}</td>
              <td>${t.salesPersonName || '-'}</td>
              <td class="right">${t.soTotalQty || 0}</td>
              <td class="right">${formatAmount(t.soAmount)}</td>
              <td>${t.dcCount || 0}</td>
              <td class="right">${t.dcTotalQty || 0}</td>
              <td class="right">${formatAmount(t.dcTotalAmount)}</td>
              <td>${t.siCount || 0}</td>
              <td class="right">${t.siTotalQty || 0}</td>
              <td class="right">${formatAmount(t.siTotalAmount)}</td>
              <td>
                <span class="progress-bar"><span class="progress-fill" style="width:${Math.min(t.fulfillmentPct, 100)}%;background:${getFulfillmentColor(t.fulfillmentPct)}"></span></span>
                ${t.fulfillmentPct.toFixed(1)}%
              </td>
              <td>${t.status || '-'}</td>
            </tr>`;
            // Item-wise detail rows
            if (t.itemBreakdown && t.itemBreakdown.length > 0) {
              t.itemBreakdown.forEach(item => {
                const iPct = Math.max(item.deliveredPct, item.invoicedPct).toFixed(0);
                const iColor = iPct >= 100 ? '#28A745' : iPct > 0 ? '#FFC107' : '#DC3545';
                rows += `
                <tr style="background:#f9f9f9;font-size:10px;color:#555">
                  <td></td><td></td>
                  <td colspan="2" style="padding-left:20px">${item.itemCode ? item.itemCode + ' - ' : ''}${item.itemName || '-'} ${item.uom ? '(' + item.uom + ')' : ''}</td>
                  <td class="right">${item.orderedQty}</td><td></td>
                  <td></td><td class="right" style="color:${item.deliveredQty > 0 ? '#28A745' : ''}">${item.deliveredQty}</td><td></td>
                  <td></td><td class="right">${item.invoicedQty}</td><td></td>
                  <td><span class="progress-bar" style="width:40px"><span class="progress-fill" style="width:${iPct}%;background:${iColor}"></span></span> ${iPct}%</td>
                  <td style="color:${item.pendingQty > 0 ? '#DC3545' : item.closedQty > 0 ? '#546E7A' : '#28A745'}">${item.pendingQty > 0 ? item.pendingQty + ' pending' : item.closedQty > 0 ? item.closedQty + ' closed' : 'Done'}</td>
                </tr>`;
              });
            }
            return rows;
          }).join('')}
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>
                {entry.dataKey === 'count' || entry.dataKey === 'value'
                  ? (entry.dataKey === 'count' ? formatNumber(entry.value) : formatCurrency(entry.value))
                  : formatCurrency(entry.value)
                }
              </strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{entry.name}</ChartTooltipLabel>
          <ChartTooltipRow>
            <strong>{entry.value} SO{entry.value !== 1 ? 's' : ''}</strong>
          </ChartTooltipRow>
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const BarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  // Table totals
  const tableTotals = useMemo(() => {
    return sortedTransactions.reduce((totals, t) => {
      totals.soTotalQty += t.soTotalQty || 0;
      totals.soAmount += t.soAmount || 0;
      totals.dcCount += t.dcCount || 0;
      totals.dcTotalQty += t.dcTotalQty || 0;
      totals.dcTotalAmount += t.dcTotalAmount || 0;
      totals.siCount += t.siCount || 0;
      totals.siTotalQty += t.siTotalQty || 0;
      totals.siTotalAmount += t.siTotalAmount || 0;
      return totals;
    }, { soTotalQty: 0, soAmount: 0, dcCount: 0, dcTotalQty: 0, dcTotalAmount: 0, siCount: 0, siTotalQty: 0, siTotalAmount: 0 });
  }, [sortedTransactions]);

  return (
    <PageLayout pageTitle="SO Tracking" breadcrumb="Reports / Tracking / SO Tracking">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomer}
              onChange={setSelectedCustomer}
              allOption={{ value: '', label: 'All Customers' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Salesperson</Label>
            <Select value={selectedSalesperson} onChange={e => setSelectedSalesperson(e.target.value)}>
              <option value="">All Salespersons</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeCode ? `${emp.employeeCode} - ` : ''}{emp.fullName || emp.firstName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
              <option value="All">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Approved">Approved</option>
              <option value="Partially Delivered">Partially Delivered</option>
              <option value="Completed">Completed</option>
              <option value="Closed">Closed</option>
              <option value="Cancelled">Cancelled</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Fulfillment</Label>
            <Select value={selectedFulfillment} onChange={e => setSelectedFulfillment(e.target.value)}>
              <option value="All">All</option>
              <option value="Open">Open (0%)</option>
              <option value="Partially Fulfilled">Partially Fulfilled</option>
              <option value="Fully Fulfilled">Fully Fulfilled (100%)</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={sortedTransactions.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={sortedTransactions.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadTransactions} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiFileText /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total SOs</SummaryLabel>
              <SummaryValue>{formatNumber(summary.totalSOs)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#17A2B8"><FiClock /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Open SOs</SummaryLabel>
              <SummaryValue style={{ color: '#17A2B8' }}>{formatNumber(summary.openSOs)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#FFC107"><FiTruck /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Partially Delivered</SummaryLabel>
              <SummaryValue style={{ color: '#FFC107' }}>{formatNumber(summary.partiallyDelivered)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiCheckCircle /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Completed</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{formatNumber(summary.completed)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent3}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total SO Value</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalSOValue)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiTruck /></EmptyIcon>
              <EmptyText>Loading sale order data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiTruck /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view SO tracking</EmptyText>
            </EmptyState>
          ) : sortedTransactions.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiTruck /></EmptyIcon>
              <EmptyText>No sale orders found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: '30px' }}></Th>
                  <Th $sortable onClick={() => handleSort('docDate')}>Date{getSortIndicator('docDate')}</Th>
                  <Th $sortable onClick={() => handleSort('docNumber')}>SO #{getSortIndicator('docNumber')}</Th>
                  <Th $sortable onClick={() => handleSort('partyName')}>Customer{getSortIndicator('partyName')}</Th>
                  <Th $sortable onClick={() => handleSort('salesPersonName')}>Salesperson{getSortIndicator('salesPersonName')}</Th>
                  <Th $sortable $right onClick={() => handleSort('soTotalQty')}>Ordered Qty{getSortIndicator('soTotalQty')}</Th>
                  <Th $sortable $right onClick={() => handleSort('soAmount')}>SO Amount{getSortIndicator('soAmount')}</Th>
                  <Th $sortable onClick={() => handleSort('dcCount')}>DC{getSortIndicator('dcCount')}</Th>
                  <Th $sortable $right onClick={() => handleSort('dcTotalQty')}>Delivered Qty{getSortIndicator('dcTotalQty')}</Th>
                  <Th $sortable $right onClick={() => handleSort('dcTotalAmount')}>Delivered Amt{getSortIndicator('dcTotalAmount')}</Th>
                  <Th $sortable onClick={() => handleSort('siCount')}>Inv{getSortIndicator('siCount')}</Th>
                  <Th $sortable $right onClick={() => handleSort('siTotalQty')}>Invoiced Qty{getSortIndicator('siTotalQty')}</Th>
                  <Th $sortable $right onClick={() => handleSort('siTotalAmount')}>Invoiced Amt{getSortIndicator('siTotalAmount')}</Th>
                  <Th $sortable onClick={() => handleSort('fulfillmentPct')}>Fulfillment %{getSortIndicator('fulfillmentPct')}</Th>
                  <Th $sortable onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedTransactions.map((t, index) => (
                  <React.Fragment key={t.id || index}>
                    <Tr $clickable onClick={() => toggleRow(t.id)}>
                      <Td style={{ width: '30px', cursor: 'pointer' }}>
                        {(t.dcCount > 0 || t.siCount > 0) && (
                          expandedRows[t.id]
                            ? <FiChevronDown size={14} />
                            : <FiChevronRight size={14} />
                        )}
                      </Td>
                      <Td>{formatDateStr(t.docDate)}</Td>
                      <Td>
                        <DocNumber onClick={(e) => { e.stopPropagation(); handleSOClick(t); }} title="Click to open SO">
                          {t.docNumber || '-'} <FiExternalLink size={11} />
                        </DocNumber>
                        {(t.status !== 'Completed' && t.status !== 'Cancelled' && t.status !== 'Closed' && (t.fulfillmentPct || 0) < 100) && (
                          <ChildDocBtnGroup onClick={(e) => e.stopPropagation()}>
                            <ChildDocBtn
                              title="Create Delivery Challan against this SO"
                              onClick={() => navigate('/sales/delivery-challan', {
                                state: {
                                  prefill: {
                                    parentId: t.id,
                                    parentDocNumber: t.docNumber,
                                    parentTranType: 'SO',
                                    partyId: t.partyId,
                                    partyName: t.partyName,
                                    lines: t.lines || []
                                  }
                                }
                              })}
                            >+ DC</ChildDocBtn>
                            <ChildDocBtn
                              title="Create Sale Invoice against this SO"
                              onClick={() => navigate('/sales/sale-invoice', {
                                state: {
                                  prefill: {
                                    parentId: t.id,
                                    parentDocNumber: t.docNumber,
                                    parentTranType: 'SO',
                                    partyId: t.partyId,
                                    partyName: t.partyName,
                                    lines: t.lines || []
                                  }
                                }
                              })}
                            >+ SI</ChildDocBtn>
                          </ChildDocBtnGroup>
                        )}
                      </Td>
                      <Td>{t.partyName || '-'}</Td>
                      <Td>{t.salesPersonName || '-'}</Td>
                      <AmountCell $right>{t.soTotalQty || 0}</AmountCell>
                      <AmountCell $right>{formatAmount(t.soAmount)}</AmountCell>
                      <Td style={{ textAlign: 'center' }}>{t.dcCount || 0}</Td>
                      <AmountCell $right>{t.dcTotalQty || 0}</AmountCell>
                      <AmountCell $right>{formatAmount(t.dcTotalAmount)}</AmountCell>
                      <Td style={{ textAlign: 'center' }}>{t.siCount || 0}</Td>
                      <AmountCell $right>{t.siTotalQty || 0}</AmountCell>
                      <AmountCell $right>{formatAmount(t.siTotalAmount)}</AmountCell>
                      <Td>
                        <FulfillmentCell>
                          <ProgressBarWrapper>
                            <ProgressBar
                              $width={Math.min(t.fulfillmentPct, 100)}
                              $color={getFulfillmentColor(t.fulfillmentPct)}
                            />
                          </ProgressBarWrapper>
                          <FulfillmentText $color={getFulfillmentColor(t.fulfillmentPct)}>
                            {t.fulfillmentPct.toFixed(1)}%
                          </FulfillmentText>
                        </FulfillmentCell>
                      </Td>
                      <Td>
                        <StatusBadge $status={t.status}>
                          {STATUS_CONFIG[t.status]?.label || t.status || '-'}
                        </StatusBadge>
                      </Td>
                    </Tr>

                    {/* Expanded Row Detail */}
                    {expandedRows[t.id] && (
                      <ExpandedRow>
                        <Td colSpan={15}>
                          <ExpandedContent>
                            {/* Item-wise Breakdown */}
                            {t.itemBreakdown && t.itemBreakdown.length > 0 && (
                              <DetailSection>
                                <DetailTitle>Item-wise Tracking</DetailTitle>
                                <DetailTable>
                                  <thead>
                                    <tr>
                                      <DetailTh>Item Code</DetailTh>
                                      <DetailTh>Item Name</DetailTh>
                                      <DetailTh>UOM</DetailTh>
                                      <DetailTh $right>Ordered</DetailTh>
                                      <DetailTh $right>Delivered</DetailTh>
                                      <DetailTh $right>Invoiced</DetailTh>
                                      <DetailTh $right>Pending</DetailTh>
                                      <DetailTh>Fulfilled %</DetailTh>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {t.itemBreakdown.map((item, idx) => (
                                      <tr key={item.itemId || idx}>
                                        <DetailTd style={{ fontWeight: 500 }}>{item.itemCode || '-'}</DetailTd>
                                        <DetailTd>{item.itemName || '-'}</DetailTd>
                                        <DetailTd>{item.uom || '-'}</DetailTd>
                                        <DetailTd $right>{item.orderedQty}</DetailTd>
                                        <DetailTd $right style={{ color: item.deliveredQty > 0 ? '#28A745' : undefined }}>
                                          {item.deliveredQty}
                                        </DetailTd>
                                        <DetailTd $right>{item.invoicedQty}</DetailTd>
                                        <DetailTd $right style={{ color: item.pendingQty > 0 ? '#DC3545' : item.closedQty > 0 ? '#546E7A' : '#28A745' }}>
                                          {item.closedQty > 0 ? `${item.closedQty} closed` : item.pendingQty}
                                        </DetailTd>
                                        <DetailTd>
                                          <FulfillmentCell>
                                            <ProgressBarWrapper style={{ width: 50 }}>
                                              <ProgressBar
                                                $width={Math.min(Math.max(item.deliveredPct, item.invoicedPct), 100)}
                                                $color={getFulfillmentColor(Math.max(item.deliveredPct, item.invoicedPct))}
                                              />
                                            </ProgressBarWrapper>
                                            <FulfillmentText $color={getFulfillmentColor(Math.max(item.deliveredPct, item.invoicedPct))}>
                                              {Math.max(item.deliveredPct, item.invoicedPct).toFixed(0)}%
                                            </FulfillmentText>
                                          </FulfillmentCell>
                                        </DetailTd>
                                      </tr>
                                    ))}
                                  </tbody>
                                </DetailTable>
                              </DetailSection>
                            )}

                            {/* Linked DCs */}
                            {t.dcs && t.dcs.length > 0 && (
                              <DetailSection>
                                <DetailTitle>Delivery Challans ({t.dcs.length})</DetailTitle>
                                <DetailTable>
                                  <thead>
                                    <tr>
                                      <DetailTh>DC #</DetailTh>
                                      <DetailTh>Date</DetailTh>
                                      <DetailTh $right>Qty</DetailTh>
                                      <DetailTh $right>Amount</DetailTh>
                                      <DetailTh>Status</DetailTh>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {t.dcs.map((dc, dcIdx) => {
                                      const dcQty = (dc.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
                                      return (
                                      <tr key={dc.id || dcIdx}>
                                        <DetailTd>
                                          <DocNumberSmall onClick={(e) => { e.stopPropagation(); handleDCClick(dc); }}>
                                            {dc.docNumber || '-'} <FiExternalLink size={10} />
                                          </DocNumberSmall>
                                        </DetailTd>
                                        <DetailTd>{formatDateStr(dc.docDate)}</DetailTd>
                                        <DetailTd $right>{dcQty}</DetailTd>
                                        <DetailTd $right>{formatAmount(Number(dc.totalAmount) || Number(dc.netAmount) || 0)}</DetailTd>
                                        <DetailTd>
                                          <StatusBadgeSmall>{dc.status || '-'}</StatusBadgeSmall>
                                        </DetailTd>
                                      </tr>
                                      );
                                    })}
                                  </tbody>
                                </DetailTable>
                              </DetailSection>
                            )}

                            {/* Linked SIs */}
                            {t.sis && t.sis.length > 0 && (
                              <DetailSection>
                                <DetailTitle>Sale Invoices ({t.sis.length})</DetailTitle>
                                <DetailTable>
                                  <thead>
                                    <tr>
                                      <DetailTh>Invoice #</DetailTh>
                                      <DetailTh>Date</DetailTh>
                                      <DetailTh $right>Qty</DetailTh>
                                      <DetailTh $right>Amount</DetailTh>
                                      <DetailTh>Status</DetailTh>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {t.sis.map((si, siIdx) => {
                                      const siQty = si.soQty ?? (si.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
                                      return (
                                      <tr key={si.id || siIdx}>
                                        <DetailTd>
                                          <DocNumberSmall onClick={(e) => { e.stopPropagation(); handleSIClick(si); }}>
                                            {si.docNumber || '-'} <FiExternalLink size={10} />
                                          </DocNumberSmall>
                                        </DetailTd>
                                        <DetailTd>{formatDateStr(si.docDate)}</DetailTd>
                                        <DetailTd $right>{siQty}</DetailTd>
                                        <DetailTd $right>{formatAmount(si.soAmount ?? (Number(si.totalAmount) || Number(si.netAmount) || 0))}</DetailTd>
                                        <DetailTd>
                                          <StatusBadgeSmall>{si.status || '-'}</StatusBadgeSmall>
                                        </DetailTd>
                                      </tr>
                                      );
                                    })}
                                  </tbody>
                                </DetailTable>
                              </DetailSection>
                            )}
                          </ExpandedContent>
                        </Td>
                      </ExpandedRow>
                    )}
                  </React.Fragment>
                ))}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedTransactions.length} records)
                  </Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{tableTotals.soTotalQty}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.soAmount)}</AmountCell>
                  <Td style={{ textAlign: 'center', fontWeight: 700 }}>{tableTotals.dcCount}</Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{tableTotals.dcTotalQty}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.dcTotalAmount)}</AmountCell>
                  <Td style={{ textAlign: 'center', fontWeight: 700 }}>{tableTotals.siCount}</Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{tableTotals.siTotalQty}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.siTotalAmount)}</AmountCell>
                  <Td></Td>
                  <Td></Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasSearched && !loading && filteredTransactions.length > 0 && (
        <ChartsRow>
          {/* Pie chart: SO status distribution */}
          <ChartSection>
            <ChartTitle>SO Status Distribution</ChartTitle>
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {statusChartData.map((entry, index) => {
                      const statusColor = STATUS_CONFIG[entry.name]?.color || CHART_COLORS[index % CHART_COLORS.length];
                      return <Cell key={`cell-${index}`} fill={statusColor} />;
                    })}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No status data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Bar chart: SO value by customer (top 10) */}
          <ChartSection>
            <ChartTitle>Top 10 Customers by SO Value</ChartTitle>
            {customerChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={customerChartData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke={colors.text.secondary} width={120} />
                  <Tooltip content={<BarTooltip />} />
                  <Bar dataKey="amount" name="SO Value" fill={colors.chart.primary} radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No customer data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Line chart: SO trend over time */}
          <ChartSection $wide>
            <ChartTitle>SO Trend Over Time (Monthly)</ChartTitle>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    stroke={colors.text.secondary}
                    tickFormatter={(v) => formatNumber(v, { compact: true })}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    stroke={colors.text.secondary}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="value"
                    name="SO Value"
                    stroke={colors.chart.primary}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="count"
                    name="SO Count"
                    stroke={colors.chart.accent3}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No trend data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  ${props => props.$clickable && `
    cursor: pointer;
  `}

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const ExpandedRow = styled.tr`
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const ExpandedContent = styled.div`
  display: flex;
  gap: 24px;
  padding: 12px 8px;
  flex-wrap: wrap;
`;

const DetailSection = styled.div`
  flex: 1;
  min-width: 280px;
`;

const DetailTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.primary.main};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: ${colors.borderRadius.md};
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
`;

const DetailTh = styled.th`
  padding: 8px 12px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 10px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  background: ${colors.neutral[100]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const DetailTd = styled.td`
  padding: 7px 12px;
  font-size: 12px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
  border-bottom: 1px solid ${colors.neutral[100]};
  font-family: ${props => props.$right ? "'SF Mono', 'Consolas', 'Courier New', monospace" : 'inherit'};
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
`;

const ChildDocBtnGroup = styled.div`
  display: inline-flex; gap: 4px; margin-left: 8px;
`;
const ChildDocBtn = styled.button`
  font-size: 10px;
  padding: 2px 8px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}40;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  &:hover { background: ${colors.primary.main}; color: white; }
`;

const DocNumber = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.5;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const DocNumberSmall = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 4px;
  border-radius: 3px;
  transition: all 0.2s;
  font-size: 12px;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.5;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => STATUS_CONFIG[props.$status]?.bg || colors.neutral[100]};
  color: ${props => STATUS_CONFIG[props.$status]?.color || colors.text.secondary};
`;

const StatusBadgeSmall = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  background: ${colors.neutral[100]};
  color: ${colors.text.secondary};
`;

const FulfillmentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBarWrapper = styled.div`
  width: 60px;
  height: 6px;
  background: ${colors.neutral[200]};
  border-radius: 3px;
  overflow: hidden;
  flex-shrink: 0;
`;

const ProgressBar = styled.div`
  height: 100%;
  width: ${props => props.$width || 0}%;
  background: ${props => props.$color || colors.primary.main};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const FulfillmentText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.$color || colors.text.primary};
  min-width: 45px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default SOTracking;
